import React, { useMemo } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import 'highcharts/modules/boost';

import {
  ChartCompetitor,
  TimeseriesDataPoint,
} from '../types';
import {
  CompetitorColors,
  generateCompetitorColors,
  getCompetitorSeries,
} from '../utils/generators';
import { sortMetricsByDate } from '../utils/sorters';
import { getPerformanceBaseChartConfig } from '../utils/config';

/* ---------- constants ---------- */

const OUR_RANK_COLOR = '#2563EB';
const MEDIAN_RANK_COLOR = '#475569';
const TOTAL_PROPERTIES_COLOR = '#94A3B8';

const baseConfig = getPerformanceBaseChartConfig();

/* ---------- component ---------- */

interface RankChartProps {
  propertyDatapoints: TimeseriesDataPoint[];
  competitors: ChartCompetitor[];
}

const RankChart: React.FC<RankChartProps> = ({
  propertyDatapoints,
  competitors,
}) => {
  const competitorColors = useMemo(
    () => generateCompetitorColors(competitors),
    [competitors],
  );

  const options = useMemo(
    () => createRankChartOptions(propertyDatapoints, competitors, competitorColors),
    [propertyDatapoints, competitors, competitorColors],
  );

  return (
    <div className="flex-1 min-w-[750px]">
      {Object.keys(options).length ? (
        <HighchartsReact highcharts={Highcharts} options={options} />
      ) : (
        <div className="bg-muted rounded-md flex items-center justify-center p-8">
          <p className="text-muted-foreground">No rank data available</p>
        </div>
      )}
    </div>
  );
};

/* ---------- chart builder ---------- */

const createRankChartOptions = (
  propertyDataPoints: TimeseriesDataPoint[],
  competitors: ChartCompetitor[],
  competitorColors: CompetitorColors,
): Highcharts.Options => {
  if (!propertyDataPoints?.length) return {} as Highcharts.Options;

  const dashStyle = 'Solid';
  const markerSymbol = 'circle';

  // Sort metrics by date
  const sortedMetrics = sortMetricsByDate(propertyDataPoints);

  // Find the maximum total properties value for Y-axis scaling
  const maxTotal = Math.max(...propertyDataPoints.map(m => m.total_properties));

  /* -------- build series -------- */

  const series: Highcharts.SeriesOptionsType[] = [
    /** total properties */
    {
      name: 'Total Properties',
      type: 'line',
      data: sortedMetrics.map(m => [Date.parse(m.date), m.total_properties]),
      dashStyle: 'ShortDot',
      color: TOTAL_PROPERTIES_COLOR,
      zIndex: 1,
      lineWidth: 2,
      connectNulls: true,
      boostThreshold: 1,
    },
    /** median rank */
    {
      name: 'Competitor Median Rank',
      type: 'line',
      data: sortedMetrics.map(m => [Date.parse(m.date), m.rank.median]),
      color: MEDIAN_RANK_COLOR,
      zIndex: 3,
      lineWidth: 3,
      dashStyle,
      connectNulls: true,
      boostThreshold: 1,
    },
    /** our rank */
    {
      name: 'Our Rank',
      type: 'line',
      data: sortedMetrics.map(m => [Date.parse(m.date), m.rank.our]), // Remove conditional coloring
      color: OUR_RANK_COLOR, // Always blue (#2563EB)
      zIndex: 4,
      lineWidth: 4,
      dashStyle,
      marker: { radius: 5, symbol: markerSymbol, lineWidth: 2 },
      connectNulls: true,
      boostThreshold: 1,
    },
  ];

  // Add competitor series using the updated function
  const competitorSeries = getCompetitorSeries(
    sortedMetrics,
    competitors,
    competitorColors,
    'rank'
  );

  series.push(...competitorSeries);

  /* -------- options -------- */

  return {
    ...baseConfig,
    chart: { ...baseConfig.chart },
    title: {
      text: 'Rank Comparison',
      style: { fontWeight: 'bold' },
    },
    yAxis: {
      title: { text: 'Rank', style: { color: OUR_RANK_COLOR, fontWeight: 'bold' } },
      labels: { style: { color: OUR_RANK_COLOR } },
      reversed: true,  // Higher rank numbers are worse (lower on the page), so reverse the axis
      max: maxTotal,
      endOnTick: false,
      minPadding: 0.2,
      maxPadding: 0.2,
    },
    plotOptions: {
      ...baseConfig.plotOptions,
      line: {
        ...((baseConfig.plotOptions as any)?.line || {}),
        pointPlacement: 'on',
        lineWidth: 3,
      },
      series: {
        ...(baseConfig.plotOptions as any)?.series,
        boostThreshold: 1,
      },
    },
    tooltip: {
      shared: true,
      crosshairs: true,
      animation: false,
      hideDelay: 0,
      formatter: function () {
        // @ts-ignore
        const x = this.x as number;
        let html = `<b>${Highcharts.dateFormat('%b %d, %Y', x)}</b><br/>`;

        // @ts-ignore
        const pts: any[] = this.points || [];

        const total = pts.find(p => p.series.name === 'Total Properties');
        if (total) {
          html += `<span style="color:${total.color}">●</span> ${total.series.name}: <b>${total.y}</b><br/>`;
        }

        html += '<hr style="margin:4px 0"/>';

        const our = pts.find(p => p.series.name === 'Our Rank');
        const median = pts.find(p => p.series.name === 'Competitor Median Rank');

        if (our) {
          html += `<span style="color:${our.color}">●</span> Our Rank: <b>${our.y}</b><br/>`;
        }
        if (median) {
          html += `<span style="color:${median.color}">●</span> Median Rank: <b>${median.y}</b><br/>`;
        }

        pts.forEach(p => {
          if (
            p.series.name !== 'Our Rank' &&
            p.series.name !== 'Competitor Median Rank' &&
            p.series.name !== 'Total Properties'
          ) {
            html += `<span style="color:${p.color}">●</span> ${p.series.name}: ${p.y}<br/>`;
          }
        });

        return html;
      },
    },
    series,
  } as Highcharts.Options;
};

export default RankChart;